// ═══════════════════════════════════════════════════════════════
//  Layout — shell global de l'application
//  v1.12.0 : ajout du bouton 🔑 "Mot de passe" dans le header.
//  - Ouvre la page ChangePasswordPage en mode "free".
//  - Visible pour TOUS les utilisateurs connectés (chacun peut
//    changer son propre mdp).
// ═══════════════════════════════════════════════════════════════
import { EPJ, font, globalCss } from "./theme";
import { LOGO_HEADER, BG_LOGIN } from "./logo";
import { useAuth } from "./AuthContext";
import { useData } from "./DataContext";
import { can } from "./permissions";
import { useViewportWidth } from "./useViewport";

export function Layout({
  children, currentModule, onHome, onBack, appearance,
  onOpenAdmin, onChangePassword,
}) {
  const { user, logout } = useAuth();
  const cockpit = appearance === "cockpit";

  // Cadre habituel 1320/520 ; le cockpit Direction dispose d’un cadre
  // dédié plus large, activé explicitement par App.
  const fullWidth = useViewportWidth() > 760;

  return (
    <>
      <style>{globalCss}</style>
      <div className={cockpit ? "epj-layout-cockpit" : undefined} style={{
        minHeight: "100vh",
        background: "var(--layout-background, " + EPJ.gray50 + ")",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}>
        {!cockpit && <div style={{
          position: "fixed", inset: 0, zIndex: 0,
          backgroundImage: `url(${BG_LOGIN})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          opacity: 0.10,
          filter: "saturate(1.2)",
          pointerEvents: "none",
        }}/>}

        {user && (
          <Header
            user={user}
            currentModule={currentModule}
            onHome={onHome}
            onBack={onBack}
            onLogout={logout}
            onOpenAdmin={onOpenAdmin}
            onChangePassword={onChangePassword}
            fullWidth={fullWidth}
            cockpit={cockpit}
          />
        )}

        <main style={{
          flex: 1,
          width: "100%",
          maxWidth: cockpit ? 1360 : (fullWidth ? 1320 : 520),
          margin: "0 auto",
          padding: cockpit ? "0 env(safe-area-inset-right) 0 env(safe-area-inset-left)" : user
            ? "0 max(16px, env(safe-area-inset-left)) 40px max(16px, env(safe-area-inset-right))"
            : 0,
          position: "relative",
          zIndex: 1,
        }}>
          {children}
        </main>
      </div>
    </>
  );
}

function Header({ user, currentModule, onHome, onBack, onLogout, onOpenAdmin, onChangePassword, fullWidth = false, cockpit = false }) {
  const { rolesConfig } = useData();
  const isAdmin = can(user, "_admin", null, rolesConfig);

  const handleLogout = () => {
    const ok = window.confirm(
      "Voulez-vous vraiment vous déconnecter ?\n\n" +
      "Toute saisie en cours sera sauvegardée automatiquement et restaurée à votre prochaine connexion."
    );
    if (ok) onLogout();
  };

  const showBack = typeof onBack === "function";

  return (
    <header style={{
      position: "sticky", top: 0, zIndex: 100,
      background: "var(--layout-header, rgba(255,255,255,.96))",
      backdropFilter: "blur(8px)",
      WebkitBackdropFilter: "blur(8px)",
      borderBottom: `1px solid var(--layout-border, ${EPJ.gray200})`,
      paddingTop: cockpit && fullWidth ? "env(safe-area-inset-top, 0px)" : "var(--safe-top, 44px)",
      paddingLeft: "env(safe-area-inset-left)",
      paddingRight: "env(safe-area-inset-right)",
    }}>
      <div style={{
        maxWidth: cockpit ? 1360 : (fullWidth ? 1320 : 520), margin: "0 auto",
        padding: "10px 12px",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        {showBack && (
          <button
            onClick={onBack}
            style={{
              background: EPJ.dark,
              border: "none",
              color: EPJ.white,
              borderRadius: 10,
              height: 44,
              padding: "0 14px",
              fontSize: 13, fontWeight: 700, lineHeight: 1,
              cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 6,
              flexShrink: 0,
              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
              fontFamily: font.body,
              letterSpacing: 0.2,
            }}
            title="Retour à l'accueil de l'application"
            aria-label="Retour à l'accueil de l'application"
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>🏠</span>
            <span>Accueil</span>
          </button>
        )}

        <button
          onClick={onHome}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            padding: 0, display: "flex", alignItems: "center", gap: 10,
            flex: 1, minWidth: 0, textAlign: "left",
          }}
          title="Retour à l'accueil"
        >
          <img
            src={LOGO_HEADER}
            alt="EPJ Électricité Générale"
            style={{ height: 28, width: "auto", display: "block", flexShrink: 0,
              background: cockpit ? EPJ.white : undefined, borderRadius: cockpit ? 4 : undefined }}
          />
          <div style={{
            minWidth: 0, display: "flex", flexDirection: "column",
            lineHeight: 1.2, justifyContent: "center",
          }}>
            <div style={{
              fontSize: 10, fontWeight: 600, color: `var(--layout-muted, ${EPJ.gray500})`,
              letterSpacing: 0.8, textTransform: "uppercase",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {currentModule || "Application interne"}
            </div>
            <div style={{
              fontSize: 11, fontWeight: 600, color: `var(--layout-text, ${EPJ.gray900})`,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {user.prenom} {user.nom}
            </div>
          </div>
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          {isAdmin && onOpenAdmin && (
            <button
              onClick={onOpenAdmin}
              style={{
                background: EPJ.gray900, border: "none", color: EPJ.white,
                borderRadius: 10, padding: 0, fontSize: 18, fontWeight: 600,
                cursor: "pointer", fontFamily: font.body,
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 40, height: 40,
              }}
              title="Administration"
              aria-label="Administration"
            >
              ⚙
            </button>
          )}

          {/* v1.12.0 — Bouton changement mdp */}
          {onChangePassword && (
            <button
              onClick={onChangePassword}
              style={{
                background: EPJ.gray700 || EPJ.gray700, border: "none", color: EPJ.white,
                borderRadius: 10, padding: 0, fontSize: 16, fontWeight: 600,
                cursor: "pointer", fontFamily: font.body,
                display: "flex", alignItems: "center", justifyContent: "center",
                width: 40, height: 40,
              }}
              title="Changer mon mot de passe"
              aria-label="Changer mon mot de passe"
            >
              🔑
            </button>
          )}

          <button
            onClick={handleLogout}
            style={{
              background: EPJ.red, border: "none", color: EPJ.white,
              borderRadius: 10, padding: "0 12px",
              fontSize: 12, fontWeight: 700,
              cursor: "pointer", fontFamily: font.body,
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: 5, height: 40,
              boxShadow: "0 1px 3px rgba(229,57,53,0.35)",
              letterSpacing: 0.3,
            }}
            title="Se déconnecter"
            aria-label="Se déconnecter"
          >
            <span style={{ fontSize: 16, lineHeight: 1 }}>⏻</span>
            <span>Déconnexion</span>
          </button>
        </div>
      </div>
    </header>
  );
}
